import { useState, useEffect } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useUser } from '@/contexts/UserContext.tsx';

interface IGeneralSettingsProps {
  title: string;
}

const initialValues = {
  firstName: '',
  lastName: '',
  email: ''
};

const settingsSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  lastName: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .required('Last name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required')
});

const BasicSettings = ({ title }: IGeneralSettingsProps) => {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const API_URL = import.meta.env.VITE_APP_API_URL;
  const API_USER = `${API_URL}/user`;
  const { refreshUser } = useUser();

  const formik = useFormik({
    initialValues,
    validationSchema: settingsSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitLoading(true);
      try {
        await axios.put(API_USER, {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email
        });

        await refreshUser();
        enqueueSnackbar('Benutzerdaten wurden erfolgreich aktualisiert.', {
          variant: 'success'
        });
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Ein unerwarteter Fehler ist aufgetreten.';
        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSubmitting(false);
        setSubmitLoading(false);
      }
    }
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(API_USER, {
          headers: { Accept: 'application/json' }
        });
        const userData = response.data;

        formik.setValues({
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || ''
        });
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Benutzerdaten konnten nicht geladen werden.';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div>Benutzerdaten werden geladen …</div>;
  }

  return (
    <div className="card pb-2.5">
      <form onSubmit={formik.handleSubmit}>
        <div className="card-header" id="general_settings">
          <h3 className="card-title">{title}</h3>
        </div>

        <div className="card-body grid gap-5">
          {/* Vorname */}
          <div className="grid gap-1">
            <label className="form-label">Vorname</label>
            <input
              type="text"
              className="input"
              placeholder="Vorname"
              {...formik.getFieldProps('firstName')}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.firstName}
              </span>
            )}
          </div>

          {/* Nachname */}
          <div className="grid gap-1">
            <label className="form-label">Nachname</label>
            <input
              type="text"
              className="input"
              placeholder="Nachname"
              {...formik.getFieldProps('lastName')}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.lastName}
              </span>
            )}
          </div>

          {/* E-Mail */}
          <div className="grid gap-1">
            <label className="form-label">E-Mail</label>
            <input
              type="email"
              className="input"
              placeholder="email@example.com"
              {...formik.getFieldProps('email')}
            />
            {formik.touched.email && formik.errors.email && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.email}
              </span>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitLoading || formik.isSubmitting}
            >
              {submitLoading ? 'Bitte warten …' : 'Änderungen speichern'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export { BasicSettings, type IGeneralSettingsProps };
